// fsrs.ts — FSRS-4.5 spaced-repetition scheduler, ported to TypeScript CLIENT-SIDE from the
// published algorithm spec (open-spaced-repetition / FSRS v4.5 — the free-spaced-repetition-
// scheduler algorithm definition). This is a CLEAN-ROOM port of the public formulas + default
// parameters; no GPL/AGPL code was copied. All scheduling runs ON-DEVICE — the server brain
// never sees review data; it only ever receives notes text for card GENERATION (brain.ts).
//
// The model in one paragraph: every card carries a memory state (stability S = the interval at
// which recall probability is 90%, difficulty D ∈ [1,10]) plus bookkeeping (state, due, reps,
// lapses, last_review). Grading a card (Again/Hard/Good/Easy) updates S and D from the elapsed
// time and the retrievability R at review time, then schedules the next due date as the interval
// at which R will have decayed to the requested retention (0.9).

export type Grade = 1 | 2 | 3 | 4; // 1=Again · 2=Hard · 3=Good · 4=Easy
export const GRADE_LABELS: Record<Grade, string> = { 1: "Again", 2: "Hard", 3: "Good", 4: "Easy" };

// Card lifecycle states (Anki-style, as used by the FSRS reference schedulers).
export const STATE_NEW = 0;
export const STATE_LEARNING = 1;
export const STATE_REVIEW = 2;
export const STATE_RELEARNING = 3;
export type CardStateKind = typeof STATE_NEW | typeof STATE_LEARNING | typeof STATE_REVIEW | typeof STATE_RELEARNING;

/** The scheduling fields persisted per card (mirrors the columns in src-tauri/src/db.rs). */
export interface SchedulingState {
  state: number;
  /** ISO datetime the card is next due. New cards are due immediately. */
  due: string;
  /** FSRS stability (days). 0 until first graded. */
  stability: number;
  /** FSRS difficulty 1..10. 0 until first graded. */
  difficulty: number;
  reps: number;
  lapses: number;
  /** ISO datetime of the last review. null until first graded. */
  last_review: string | null;
}

// ── FSRS-4.5 default parameters (the published 17-weight vector) ────────────────
const W = [
  0.4872, 1.4003, 3.7145, 13.8206, 5.1618, 1.2298, 0.8975, 0.031, 1.6474,
  0.1367, 1.0461, 2.1072, 0.0793, 0.3246, 1.587, 0.2272, 2.8755,
] as const;

const DECAY = -0.5;
const FACTOR = 19 / 81; // chosen so R(t=S) = 0.9
const REQUEST_RETENTION = 0.9; // schedule the next review when recall probability decays to 90%
const MAX_INTERVAL_DAYS = 365; // consumer app: never schedule more than a year out

const MS_PER_DAY = 86_400_000;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Probability of recall after `elapsedDays` for a card with stability `s`. */
export function retrievability(elapsedDays: number, s: number): number {
  if (s <= 0) return 0;
  return Math.pow(1 + (FACTOR * Math.max(0, elapsedDays)) / s, DECAY);
}

/** The next interval (whole days ≥ 1) at which retrievability decays to REQUEST_RETENTION. */
export function nextIntervalDays(s: number): number {
  const raw = (s / FACTOR) * (Math.pow(REQUEST_RETENTION, 1 / DECAY) - 1);
  return clamp(Math.round(raw), 1, MAX_INTERVAL_DAYS);
}

// Initial memory state after the FIRST grade of a card.
function initStability(g: Grade): number {
  return Math.max(W[g - 1], 0.1);
}
function initDifficulty(g: Grade): number {
  return clamp(W[4] - (g - 3) * W[5], 1, 10);
}

// Difficulty update with mean reversion toward D0(Easy) — per the 4.5 reference scheduler.
function nextDifficulty(d: number, g: Grade): number {
  const next = d - W[6] * (g - 3);
  return clamp(W[7] * initDifficulty(4) + (1 - W[7]) * next, 1, 10);
}

// Stability after a SUCCESSFUL review (Hard/Good/Easy) at retrievability r.
function nextRecallStability(d: number, s: number, r: number, g: Grade): number {
  const hardPenalty = g === 2 ? W[15] : 1;
  const easyBonus = g === 4 ? W[16] : 1;
  return s * (1 + Math.exp(W[8]) * (11 - d) * Math.pow(s, -W[9]) * (Math.exp(W[10] * (1 - r)) - 1) * hardPenalty * easyBonus);
}

// Stability after a LAPSE (Again) at retrievability r. Never exceeds the pre-lapse stability.
function nextForgetStability(d: number, s: number, r: number): number {
  const sf = W[11] * Math.pow(d, -W[12]) * (Math.pow(s + 1, W[13]) - 1) * Math.exp(W[14] * (1 - r));
  return Math.min(Math.max(sf, 0.1), s);
}

/** Fresh scheduling state for a newly created card — due immediately. */
export function newSchedulingState(now: Date = new Date()): SchedulingState {
  return { state: STATE_NEW, due: now.toISOString(), stability: 0, difficulty: 0, reps: 0, lapses: 0, last_review: null };
}

export interface GradeOutcome {
  next: SchedulingState;
  /** Days until the next due (fractional for the minutes-scale learning steps). */
  scheduledDays: number;
  /** Days elapsed since the last review (0 for a first review). */
  elapsedDays: number;
}

const addMinutes = (now: Date, min: number) => new Date(now.getTime() + min * 60_000).toISOString();
const addDays = (now: Date, days: number) => new Date(now.getTime() + days * MS_PER_DAY).toISOString();

/**
 * Grade a card — the whole FSRS-4.5 step. Pure function: returns the next persisted
 * scheduling state + the numbers worth logging; the caller writes them to SQLite.
 *
 * Same-day learning steps use short minute-scale delays (the standard 1m/5m/10m ladder);
 * graduation to the Review state schedules by stability via nextIntervalDays.
 */
export function gradeCard(card: SchedulingState, g: Grade, now: Date = new Date()): GradeOutcome {
  const elapsedDays = card.last_review ? Math.max(0, (now.getTime() - new Date(card.last_review).getTime()) / MS_PER_DAY) : 0;
  const base = { reps: card.reps + 1, last_review: now.toISOString() };

  // FIRST grade of a brand-new card → seed S and D from the grade.
  if (card.state === STATE_NEW) {
    const stability = initStability(g);
    const difficulty = initDifficulty(g);
    if (g === 4) {
      const days = nextIntervalDays(stability);
      return { next: { ...base, state: STATE_REVIEW, due: addDays(now, days), stability, difficulty, lapses: card.lapses }, scheduledDays: days, elapsedDays };
    }
    const mins = g === 1 ? 1 : g === 2 ? 5 : 10;
    return { next: { ...base, state: STATE_LEARNING, due: addMinutes(now, mins), stability, difficulty, lapses: card.lapses }, scheduledDays: mins / 1440, elapsedDays };
  }

  // (RE)LEARNING — short steps until Good/Easy graduates the card to Review.
  if (card.state === STATE_LEARNING || card.state === STATE_RELEARNING) {
    const stability = card.stability > 0 ? card.stability : initStability(g);
    const difficulty = card.difficulty > 0 ? card.difficulty : initDifficulty(g);
    if (g === 1 || g === 2) {
      const mins = g === 1 ? 5 : 10;
      return { next: { ...base, state: card.state, due: addMinutes(now, mins), stability, difficulty, lapses: card.lapses }, scheduledDays: mins / 1440, elapsedDays };
    }
    const s2 = g === 4 ? stability * W[16] : stability; // easy graduation carries the easy bonus
    const days = nextIntervalDays(Math.max(s2, 0.1));
    return { next: { ...base, state: STATE_REVIEW, due: addDays(now, days), stability: Math.max(s2, 0.1), difficulty, lapses: card.lapses }, scheduledDays: days, elapsedDays };
  }

  // REVIEW — the core FSRS update from elapsed time + retrievability.
  const r = retrievability(elapsedDays, card.stability);
  const difficulty = nextDifficulty(card.difficulty, g);
  if (g === 1) {
    const stability = nextForgetStability(card.difficulty, card.stability, r);
    return {
      next: { ...base, state: STATE_RELEARNING, due: addMinutes(now, 5), stability, difficulty, lapses: card.lapses + 1 },
      scheduledDays: 5 / 1440, elapsedDays,
    };
  }
  const stability = nextRecallStability(card.difficulty, card.stability, r, g);
  let days = nextIntervalDays(stability);
  if (g === 2) days = clamp(Math.min(days, nextIntervalDays(card.stability)), 1, MAX_INTERVAL_DAYS); // Hard never schedules past the plain-recall interval
  return { next: { ...base, state: STATE_REVIEW, due: addDays(now, days), stability, difficulty, lapses: card.lapses }, scheduledDays: days, elapsedDays };
}

/** Preview label for a grade button, e.g. "10 min" / "3 d" / "2 mo". */
export function previewInterval(card: SchedulingState, g: Grade, now: Date = new Date()): string {
  const { scheduledDays } = gradeCard(card, g, now);
  if (scheduledDays < 1) {
    const mins = Math.max(1, Math.round(scheduledDays * 1440));
    return `${mins} min`;
  }
  if (scheduledDays < 30) return `${Math.round(scheduledDays)} d`;
  if (scheduledDays < 365) return `${Math.round(scheduledDays / 30)} mo`;
  return `${(scheduledDays / 365).toFixed(1)} yr`;
}

export function isDue(card: Pick<SchedulingState, "due">, now: Date = new Date()): boolean {
  return new Date(card.due).getTime() <= now.getTime();
}
